import type { CSSProperties, JSX, ReactNode } from 'react';
import type { AdminSection } from './types';

/**
 * The standard Account body — present on every portal (user-tier), so the
 * account controls (and crucially "Sign out all devices") are reachable from the
 * same place everywhere.
 *
 * Both sign-out handlers are plain buttons: the estate's sign-out-all handlers
 * already carry their own confirm (a native dialog — the same handler the header
 * SignOutMenu uses), so wrapping "all devices" in an inline confirm here would
 * double-confirm. The danger styling marks it as the consequential one.
 */
export interface AccountSectionProps {
  /** Identity line, e.g. "Signed in as name@example.com" or a role label. */
  identity?: ReactNode;
  onSignOutThisDevice: () => void | Promise<void>;
  /** Ends every session — the handler MUST carry its own confirmation. */
  onSignOutAll: () => void | Promise<void>;
  /** Extra account controls (e.g. a report-a-problem affordance). */
  extra?: ReactNode;
}

const dangerBtn: CSSProperties = { color: 'var(--color-danger, #b3261e)' };

export function AccountSection({
  identity,
  onSignOutThisDevice,
  onSignOutAll,
  extra,
}: AccountSectionProps): JSX.Element {
  return (
    <div>
      {identity != null && (
        <p style={{ fontSize: 13, color: 'var(--color-muted, #6b7280)', margin: '4px 0 12px' }}>
          {identity}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => void onSignOutThisDevice()}
        >
          Sign out this device
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={dangerBtn}
          onClick={() => void onSignOutAll()}
        >
          Sign out all devices
        </button>
      </div>
      {extra != null && <div style={{ marginTop: 12 }}>{extra}</div>}
    </div>
  );
}

/** Convenience factory → an AdminSection (Account, `user`-tier). */
export function accountSection(
  opts: AccountSectionProps & { key?: string; title?: string; icon?: ReactNode },
): AdminSection {
  const { key, title, icon, ...body } = opts;
  return {
    key: key ?? 'account',
    title: title ?? 'Account',
    ...(icon ? { icon } : {}),
    minRole: 'user',
    body: <AccountSection {...body} />,
  };
}
